import { CamundaBaseService } from './base-service';
import { resolveProcessDefinitionResourceId } from './process-definition-id';


export const CAMUNDA_RESOURCE_TYPES = {
  APPLICATION: 0,
  USER: 1,
  GROUP: 2,
  GROUP_MEMBERSHIP: 3,
  AUTHORIZATION: 4,
  FILTER: 5,
  PROCESS_DEFINITION: 6,
  TASK: 7,
  PROCESS_INSTANCE: 8,
  DEPLOYMENT: 9,
  DECISION_DEFINITION: 10,
  TENANT: 11,
  TENANT_MEMBERSHIP: 12,
  BATCH: 13,
  DECISION_REQUIREMENTS_DEFINITION: 14,
  HISTORIC_TASK: 15,
  HISTORIC_PROCESS_INSTANCE: 16,
  SYSTEM: 17,
  OPERATION_LOG_CATEGORY: 18,
  OPTIMIZE: 19,
  HISTORIC_DECISION_INSTANCE: 20
};

export const CAMUNDA_PERMISSIONS = {
  NONE: 'NONE',
  ALL: 'ALL',
  READ: 'READ',
  UPDATE: 'UPDATE',
  CREATE: 'CREATE',
  DELETE: 'DELETE',
  ACCESS: 'ACCESS',
  UPDATE_TASK: 'UPDATE_TASK',
  CREATE_INSTANCE: 'CREATE_INSTANCE',
  READ_INSTANCE: 'READ_INSTANCE',
  UPDATE_INSTANCE: 'UPDATE_INSTANCE',
  DELETE_INSTANCE: 'DELETE_INSTANCE',
  TASK_WORK: 'TASK_WORK',
  TASK_ASSIGN: 'TASK_ASSIGN'
};

export class CamundaAuthorizationService extends CamundaBaseService {
  private static async mapWithConcurrency<T, R>(
    items: T[],
    maxConcurrency: number,
    mapper: (item: T, index: number) => Promise<R>
  ): Promise<R[]> {
    if (items.length === 0) return [];

    const results: R[] = [];
    results.length = items.length;
    const workers = Math.min(Math.max(maxConcurrency, 1), items.length);
    let cursor = 0;

    const runWorker = async () => {
      while (true) {
        const index = cursor;
        cursor += 1;
        if (index >= items.length) break;
        results[index] = await mapper(items[index], index);
      }
    };

    await Promise.all(Array.from({ length: workers }, () => runWorker()));
    return results;
  }

  static async checkUserPermission(params: {
    permissionName: string;
    resourceName: string;
    resourceType: number;
    resourceId?: string;
  }): Promise<{ hasAuthorization: boolean }> {
    try {
      const queryParams = new URLSearchParams({
        permissionName: params.permissionName,
        resourceName: params.resourceName,
        resourceType: params.resourceType.toString()
      });
      
      if (params.resourceId) {
        queryParams.append('resourceId', params.resourceId);
      }
      
      const response = await this.makeRequest<{ permissionName: string; authorized: boolean }>(
        `/authorization/check?${queryParams.toString()}`,
        { method: 'GET' }
      );
      
      return {
        hasAuthorization: response.authorized
      };
      
    } catch (error) {
      console.error('Error verificando permisos:', error);
      throw error;
    }
  }



  static async checkUserTaskAssignPermission(): Promise<{ hasAuthorization: boolean }> {
    return this.checkUserPermission({
      permissionName: CAMUNDA_PERMISSIONS.TASK_ASSIGN,
      resourceName: 'PROCESS_DEFINITION',
      resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_DEFINITION
    });
  }

  static async checkUserReadTaskPermission(taskId?: string): Promise<{ hasAuthorization: boolean }> {
    return this.checkUserPermission({
      permissionName: CAMUNDA_PERMISSIONS.READ,
      resourceName: 'TASK',
      resourceType: CAMUNDA_RESOURCE_TYPES.TASK,
      resourceId: taskId
    });
  }

  static async checkUserCreateInstancePermission(processDefinitionId?: string): Promise<{ hasAuthorization: boolean }> {
    return this.checkUserPermission({
      permissionName: CAMUNDA_PERMISSIONS.CREATE_INSTANCE,
      resourceName: 'PROCESS_DEFINITION',
      resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_DEFINITION,
      resourceId: resolveProcessDefinitionResourceId(processDefinitionId)
    });
  }

  static async checkUserReadInstancePermission(processInstanceId?: string): Promise<{ hasAuthorization: boolean }> {
    return this.checkUserPermission({
      permissionName: CAMUNDA_PERMISSIONS.READ,
      resourceName: 'PROCESS_INSTANCE',
      resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_INSTANCE,
      resourceId: processInstanceId
    });
  }

  static async checkCustomPermission(
    permissionName: string, 
    resourceName: string, 
    resourceType: number, 
    resourceId?: string
  ): Promise<{ hasAuthorization: boolean }> {
    return this.checkUserPermission({
      permissionName,
      resourceName,
      resourceType,
      resourceId
    });
  }

  static async getAuthorizedResources<T extends { key: string }>(params: {
    permissionName: string;
    resourceName: string;
    resourceType: number;
    allResources: T[];
    maxConcurrency?: number;
  }): Promise<T[]> {
    try {
      const checks = await this.mapWithConcurrency(
        params.allResources,
        params.maxConcurrency ?? 6,
        async (resource) => {
          const resourceCheck = await this.checkUserPermission({
            permissionName: params.permissionName,
            resourceName: params.resourceName,
            resourceType: params.resourceType,
            resourceId: resource.key
          });
          return {
            resource,
            authorized: resourceCheck.hasAuthorization
          };
        }
      );

      const authorizedResources: T[] = [];
      checks.forEach((result) => {
        if (result.authorized) {
          authorizedResources.push(result.resource);
        }
      });

      return authorizedResources;
      
    } catch (error) {
      console.error('Error obteniendo recursos autorizados:', error);
      throw error;
    }
  }
}
